import { cn } from '@/lib/utils'
import { displayS3File } from '@/utils/s3'
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@alphabait/ui'
import { FormProps } from '@alphabait/ui/form'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { useState } from 'react'
import { Control, useController } from 'react-hook-form'
import { CustomMultiSelect as CustomMultiSelectType } from './custom-types'

// Legacy props (form module)
type LegacyProps = {
  field: CustomMultiSelectType
  formProps: FormProps
}

// Direct props (react-hook-form)
type DirectProps = {
  name: string
  control: Control<any>
  options: Array<{
    value: string
    label: string
    picture?: string | null
    disabled?: boolean
  }>
  placeholder?: string
  className?: string
  disabled?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  emptyText?: string
  showSelectedChips?: boolean
}

type CustomMultiSelectProps = LegacyProps | DirectProps

function isLegacyProps(props: CustomMultiSelectProps): props is LegacyProps {
  return 'field' in props && 'formProps' in props
}

// cmdk's default filter is fuzzy: it scores options whose letters merely appear
// in order (even with gaps) above zero, so searching "test" listed "Elegant
// Bamboo Mouse" and "bamboo" listed "Incredible Marble Bacon" (Alpha-795 QA).
// Plain case-insensitive contains instead, with a flat score so cmdk keeps the
// option order the caller passed in.
const containsFilter = (value: string, search: string) => {
  const term = search.trim().toLowerCase()
  return !term || value.toLowerCase().includes(term) ? 1 : 0
}

export function CustomMultiSelect(props: CustomMultiSelectProps) {
  const [open, setOpen] = useState(false)

  // Extract props based on mode
  const fieldName = isLegacyProps(props) ? props.field.name : props.name
  const control = isLegacyProps(props) ? props.formProps.control : props.control
  const options = isLegacyProps(props) ? props.field.options : props.options
  const placeholder = isLegacyProps(props)
    ? props?.field.uiOptions?.placeholder || 'Select options'
    : props.placeholder || 'Select options'
  const className = isLegacyProps(props)
    ? props.field.uiOptions?.className
    : props.className
  const disabled = isLegacyProps(props)
    ? props.field.uiOptions?.disabled
    : props.disabled
  const searchable = isLegacyProps(props) ? false : props.searchable
  const searchPlaceholder = isLegacyProps(props)
    ? undefined
    : props.searchPlaceholder
  const emptyText = isLegacyProps(props) ? undefined : props.emptyText
  const showSelectedChips = isLegacyProps(props)
    ? false
    : props.showSelectedChips

  const {
    field: { onChange, value = [] },
  } = useController({
    control,
    name: fieldName,
  })

  const handleUnselect = (item: string) => {
    onChange((value as string[]).filter((v) => v !== item))
  }

  const popover = (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('w-full justify-between', className)}
          disabled={disabled}
        >
          {value.length > 0
            ? value
              .map(
                (v: string) => options.find((o) => o.value === v)?.label,
              )
              .join(', ')
            : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 overflow-hidden">
        <Command className="pointer-events-auto w-full" filter={containsFilter}>
          {searchable && (
            <>
              <CommandInput placeholder={searchPlaceholder || 'Search...'} />
              <CommandEmpty>{emptyText || 'No item found.'}</CommandEmpty>
            </>
          )}
          <CommandGroup className="max-h-80 overflow-y-auto">
            {options?.map((option) => {
              const picture = 'picture' in option ? option.picture : undefined
              return (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    const newValue = Array.isArray(value) ? value : []
                    onChange(
                      newValue.includes(option.value)
                        ? newValue.filter((v) => v !== option.value)
                        : [...newValue, option.value],
                    )
                  }}
                  className="flex items-center justify-between w-full"
                  disabled={option.disabled}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {picture && (
                      <img
                        src={displayS3File(picture)}
                        alt={option.label}
                        className="h-6 w-6 shrink-0 rounded object-cover"
                      />
                    )}
                    <span className="truncate">{option.label}</span>
                  </span>
                  {value?.includes(option.value) && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </CommandItem>
              )
            })}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )

  if (!showSelectedChips) return popover

  return (
    <div className="flex flex-col gap-2">
      {popover}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {(value as string[]).map((item) => {
            const selectedOption = options.find((o) => o.value === item)
            if (!selectedOption) return null
            const picture =
              'picture' in selectedOption ? selectedOption.picture : undefined
            return (
              <Badge
                variant="outline"
                key={item}
                className="flex items-center gap-2 px-2 py-1.5 border border-border rounded-lg text-sm overflow-visible"
              >
                {picture && (
                  <img
                    src={displayS3File(picture)}
                    alt={selectedOption.label}
                    className="w-8 h-8 rounded object-cover shrink-0"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                )}
                <span className="font-normal">{selectedOption.label}</span>
                <span
                  role="button"
                  className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUnselect(item)
                    }
                  }}
                  onClick={() => handleUnselect(item)}
                >
                  <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                </span>
              </Badge>
            )
          })}
        </div>
      )}
    </div>
  )
}
